import '@patternfly/react-core/dist/styles/base-no-reset.css'; // patternfly css must be loaded first
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { LogViewer, LogViewerSearch } from '@patternfly/react-log-viewer';
import { Checkbox, Toolbar, ToolbarContent, ToolbarItem } from '@patternfly/react-core';
import { SxProps } from '@bymason-private/legacy-pattern-components';
import { useAuth } from '@bymason-private/auth-provider';
import {
  Box,
  ControllerAlert,
  Button,
  Card,
  CardActions,
  CardContent,
  Link,
  Text,
  LinearProgress,
} from '@bymason-private/legacy-pattern-components';
import { useGetLogBuffers, useXrayLogStreamConnection } from '@bymason/controller-api-hooks';
import { LegacyDetailsList, DetailItem, useToast } from '@bymason-private/controller-shared-widgets';
import { DataUsageWarningModal } from '../data-usage-warning-modal';
import { TimeoutWarningModal } from '../timeout-warning-modal';
import { TimeoutModal } from '../timeout-modal';
import { formatMegabytes, formatMinutesSeconds } from '../utils';
import { LogFilterForm } from './filter-form';
import { Theme, useTheme } from '@bymason-private/controller-v1-theme-provider';

const SECONDS_BEFORE_TIMEOUT_TO_ISSUE_WARNING = 60 * 2; // TWO MINUES
const STREAM_BUFFER_GRACE_PERIOD = 10000; // 10 SECONDS
const DURATION_AND_VIEWER_REFRESH_RATE = 1000; // 1 SECOND
const MAX_LOG_LINE_RETENTION = 5000;
const STREAM_BUFFER_FLUSH_RATE = 200; // 0.2 SECONDS
const MIN_SEARCH_CHARS = 3;
const SCROLL_OFFSET = 48;
const CLEAR_LOG_LINE_MSG = '...old log entry cleared';
let durationCounter = 0;

export const filtersReducer = (
  state: Record<string, string | string[]>,
  action: { payload: Record<string, string | string[]> }
) => {
  const newFilter = { ...state, ...action.payload };
  Object.keys(newFilter).forEach((key) => {
    if (!newFilter[key]) {
      delete newFilter[key];
    }
  });
  return newFilter;
};

export const recordToUrlSearchParams = (filters: Record<string, string | string[]>) => {
  const initParams = new URLSearchParams({});
  const filterArray = Object.entries(filters);
  if (filterArray.length === 0) {
    return undefined;
  }
  return filterArray.reduce<URLSearchParams>((acc, [key, val]) => {
    if (Array.isArray(val)) {
      val.forEach((v) => acc.append(key, v));
    } else {
      acc.set(key, val);
    }
    return acc;
  }, initParams);
};

const responsiveContentSx: SxProps<Theme> = (theme) => ({
  display: 'flex',
  columnGap: 2,
  flexWrap: 'nowrap',
  [theme.breakpoints.up('lg')]: {
    flexWrap: 'wrap',
  },
});

const contentSx: SxProps<Theme> = (theme) => ({
  flex: `0 1 ${theme.breakpoints.values.md / 2}px`,
  margin: theme.spacing(1, 0, 2, 0),
});

const logSx: SxProps<Theme> = (theme) => ({
  minHeight: 500,
  height: '80vh',
  minWidth: theme.breakpoints.values.md / 2,
  border: `1px solid ${theme.palette.grey[400]}`,
  borderRadius: 4,
  position: 'relative',
  flex: '2 2 100%',
  '& .pf-c-log-viewer__header .pf-c-toolbar': {
    position: 'absolute',
    margin: 1,
    opacity: 0.9,
    zIndex: 100,
  },
  '& .pf-c-log-viewer__header': {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: 0,
  },
  '& .pf-c-check': {
    padding: 1,
  },
  '& .pf-c-check__label': {
    width: '100%',
  },
});

export function XRayLogStream({
  deviceFamily,
  deviceId,
  deviceName,
  onSessionEnd,
  onSessionStart,
  open,
}: XRayLogStreamProps) {
  const { authToken = '' } = useAuth();
  const theme = useTheme();
  const [getLogBuffers, logBuffers] = useGetLogBuffers(authToken);
  const [
    startConnection,
    stopConnection,
    dataChannel,
    connectionInfo,
    connectionTimedOut,
    connectionError,
  ] = useXrayLogStreamConnection(deviceId, authToken);

  const logCollectionRef = useRef<LogRow[]>([]);
  const isFollowing = useRef(true);
  const isSearching = useRef(false);

  const [start, setStart] = useState(false);
  const [error, setError] = useState('');
  const [showDataUsageModal, setShowDataUsageModal] = useState(false);
  const [timeoutRemainingSec, setTimeoutRemainingSec] = useState(0);
  const [resetPendingTimeout, setResetPendingTimeout] = useState<(() => void) | null>(null);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [wrapText, setWrapText] = useState(true);
  const [disconnectedInfo, setDisconnectedInfo] = useState<DetailItem[] | null>(null);
  const [logLength, setLogLength] = useState(0);
  const [isWaitingForStreamCheck, setIsWaitingForStreamCheck] = useState(false);
  const [triggerExistingLogsRender, setTriggerExistingLogsRender] = useState(false);
  const [lastMaxEndIndex, setLastMaxEndIndex] = useState(-1);
  const [filters, dispatchFilters] = useReducer(filtersReducer, {});
  const [setToastMessage, setToastSeverity] = useToast();

  //
  // track connection state via data channel state and start flag
  // i.e., isActiveSession, isClosedSession, isPendingSession
  //
  const isActiveSession = useMemo(() => {
    return dataChannel?.readyState
      ? dataChannel.readyState === 'open' || dataChannel.readyState === 'closing'
      : false;
  }, [dataChannel?.readyState]);

  const isClosedSession = useMemo(() => {
    return dataChannel?.readyState ? dataChannel.readyState === 'closed' : true;
  }, [dataChannel?.readyState]);

  const isPendingSession = useMemo(() => {
    return start && !isActiveSession;
  }, [isActiveSession, start]);

  const logBufferList = useMemo(() => {
    return logBuffers ? logBuffers.buffers.map((buffInfo) => buffInfo.buffer) : undefined;
  }, [logBuffers]);

  const pidList = useMemo(() => {
    const bufferPids = logBuffers?.buffers.length
      ? logBuffers.buffers.find((buffInfo) => buffInfo.buffer === filters.buffer || 'main')?.chattiest || []
      : [];
    return bufferPids.map((proc) => `${proc.pid}`);
  }, [filters.buffer, logBuffers]);

  const connectionInfoItems = useMemo<DetailItem[]>(() => {
    const duration = formatMinutesSeconds(durationSeconds);
    const dataUsed = formatMegabytes(connectionInfo?.bytesReceived, connectionInfo?.bytesSent);

    return [
      { label: 'Duration', value: duration },
      { label: 'Data used', value: dataUsed },
      { label: 'Device model', value: deviceFamily },
    ];
  }, [connectionInfo?.bytesReceived, connectionInfo?.bytesSent, deviceFamily, durationSeconds]);

  const resetXray = useCallback(
    (includeError = false) => {
      setStart(false);
      setTimeoutRemainingSec(0);
      setHasTimedOut(false);
      setLastMaxEndIndex(-1);
      setDurationSeconds(0);
      if (onSessionEnd) {
        onSessionEnd();
      }
      if (includeError) setError('');
    },
    [onSessionEnd]
  );

  const resetLogData = () => {
    // clearing array this way ensures that all element
    // refs are cleared whereas setting to [] does not
    logCollectionRef.current.length = 0;
    setLogLength(0);
    setToastMessage('');
  };

  const handleLaunch = () => {
    setDisconnectedInfo(null);
    resetXray(true);
    setShowDataUsageModal(true);
  };

  const handleEndSession = useCallback(() => {
    setDisconnectedInfo(connectionInfoItems);
    resetXray(true);
  }, [connectionInfoItems, resetXray]);

  const handleDataUsageWarningClose = (proceed = false) => {
    setShowDataUsageModal(false);
    if (proceed) {
      resetLogData();
    }
    setStart(proceed);
    setIsWaitingForStreamCheck(true);
    setTimeout(() => {
      setIsWaitingForStreamCheck(false);
      if (!logCollectionRef.current.length) {
        // if after a short pause, no logs are receieved, let user know
        setToastMessage(
          `Looks like the device's logs are not yet streaming. If lines don't start appearing soon, please try ending this session and adjusting filter criteria`
        );
        setToastSeverity('warning');
      }
    }, STREAM_BUFFER_GRACE_PERIOD);
  };

  const handleTimeoutWarningClose = (timeToAddBack: number, proceed = false) => {
    durationCounter += timeToAddBack;
    setTimeoutRemainingSec(0);

    if (proceed) {
      resetPendingTimeout && resetPendingTimeout();
    } else {
      resetXray();
    }
  };

  const handleTimeoutWarning = useCallback((remainingSeconds: number, reset: () => void) => {
    setResetPendingTimeout(reset);
    setTimeoutRemainingSec(remainingSeconds);
  }, []);

  const handleTimeoutClose = () => {
    resetXray();
  };

  const handleParamsChange = (name: string, value: string | string[]) => {
    dispatchFilters({ payload: { [name]: value } });
  };

  const handleWrapChange = (checked: boolean) => {
    setWrapText(checked);
  };

  const handleSearch = (value: string) => {
    if (value.length >= MIN_SEARCH_CHARS) {
      isFollowing.current = false;
      isSearching.current = true;
    } else if (!value.length) {
      isSearching.current = false;
    }
  };

  const handleClearSearch = () => {
    isSearching.current = false;
  };

  const handleScroll = (scrollParams: OnScrollParams) => {
    if (scrollParams.scrollDirection === 'backward' && scrollParams.scrollOffsetToBottom > SCROLL_OFFSET) {
      isFollowing.current = false;
    } else if (
      scrollParams.scrollDirection === 'forward' &&
      scrollParams.scrollOffsetToBottom <= SCROLL_OFFSET
    ) {
      isFollowing.current = !isSearching.current;
    }
  };

  const appendFromLogBuffer = useCallback(
    (buffer: string[]) => {
      if (buffer.length === 0) {
        return;
      }

      logCollectionRef.current.push(...buffer);

      if (logCollectionRef.current.length + buffer.length > MAX_LOG_LINE_RETENTION) {
        // we've reached more records than we want to hold in memory, so reset
        // oldest log entry to a message constant that let's user know data has been
        // cleared; since strings are interned in JavaScript, using the same "clear"
        // message in effect clears memory as only one string instance is kept in memory.
        // finally, the reasons we don't truncate the log data array are three-fold:
        //    1. Line numbers get changed
        //    2. Results in line flash
        //    3. Causes illegible text overlap when Wrap-lines is enabled
        const maxStart = lastMaxEndIndex + 1;
        let maxEnd = -1;

        if (logCollectionRef.current.length >= MAX_LOG_LINE_RETENTION) {
          maxEnd = maxStart + buffer.length - 1;
        } else {
          maxEnd = logCollectionRef.current.length + buffer.length - MAX_LOG_LINE_RETENTION - 1;
        }

        for (let i = maxStart; i <= maxEnd; i++) {
          logCollectionRef.current[i] = CLEAR_LOG_LINE_MSG;
        }

        setLastMaxEndIndex(maxEnd);
      }

      logCollectionRef.current.push(...buffer);

      setLogLength(logCollectionRef.current.length);
    },
    [lastMaxEndIndex]
  );

  const handleStartFollowing = () => {
    isFollowing.current = true;
  };

  //
  // handle close
  //
  useEffect(() => {
    if (!open && start) {
      handleEndSession();
    }
  }, [handleEndSession, open, start]);

  useEffect(() => {
    if (deviceId && open) {
      getLogBuffers({ deviceId });
    }
  }, [deviceId, getLogBuffers, open]);

  //
  // start/stop the connection based on start flag and connection state
  //
  useEffect(() => {
    if (start && isClosedSession) {
      startConnection(recordToUrlSearchParams(filters));
    }

    if (!start && isActiveSession) {
      stopConnection();
    }
  }, [filters, isActiveSession, isClosedSession, start, startConnection, stopConnection]);

  //
  // send new filter message (not implemented on device yet)
  //
  // useEffect(() => {
  //   if (dataChannel) {
  //     dataChannel.send(JSON.stringify({feature: 'log-stream', ...filters}))
  //   }
  // }, [dataChannel, filters])

  //
  // receive messages, buffer and batch add to ref
  //
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    if (dataChannel) {
      const entriesBuffer: string[] = [];
      dataChannel.binaryType = 'arraybuffer';
      dataChannel.onmessage = (evt) => {
        const decoder = new TextDecoder('utf-8');
        const uint8 = new Uint8Array(evt.data);
        const logRow = decoder.decode(uint8);
        entriesBuffer.push(logRow);
      };

      interval = setInterval(() => {
        const pendingEntries = [...entriesBuffer];
        entriesBuffer.length = 0;
        appendFromLogBuffer(pendingEntries);
      }, STREAM_BUFFER_FLUSH_RATE);
    }

    return () => {
      // clear flush interval when closed
      interval && clearInterval(interval);
    };
  }, [appendFromLogBuffer, dataChannel]);

  //
  // handle setting flag so existing log data is
  // re-rendered if/when re-opened from tab change
  //
  useEffect(() => {
    if (!open && logLength) {
      setTriggerExistingLogsRender(true);
    }
  }, [isActiveSession, logLength, open]);

  //
  // trigger re-rendering existing logs when viewer is
  // re-opened from tab change
  //
  useEffect(() => {
    if (open && logLength && triggerExistingLogsRender) {
      setTriggerExistingLogsRender(false);
    }
  }, [logLength, open, triggerExistingLogsRender]);

  useEffect(() => {
    if (connectionError) {
      setError(connectionError);
      resetXray();
    }
  }, [connectionError, resetXray]);

  useEffect(() => {
    if (connectionTimedOut) {
      setHasTimedOut(true);
    }
  }, [connectionTimedOut]);

  useEffect(() => {
    if (
      connectionInfo &&
      connectionInfo.secondsBeforeTimeout &&
      connectionInfo.secondsBeforeTimeout <= SECONDS_BEFORE_TIMEOUT_TO_ISSUE_WARNING
    ) {
      handleTimeoutWarning(connectionInfo.secondsBeforeTimeout, () => {
        connectionInfo.resetPendingTimeout && connectionInfo.resetPendingTimeout();
      });
    }
  }, [connectionInfo, handleTimeoutWarning]);

  //
  // handle tracking/update of session duration
  // as well as triggering log viewer to refresh
  // with latest streamed data
  //
  // NOTE: this duration mechanism must be used
  // for viewer refresh because stream data is
  // held in a ref and does not trigger a render
  // when its value is set/changed.
  //
  useEffect(() => {
    let intervalId = 0;

    if (isActiveSession && !timeoutRemainingSec) {
      setDurationSeconds(durationCounter);

      intervalId = window.setInterval(() => {
        durationCounter++;
        setDurationSeconds(durationCounter / (1000 / DURATION_AND_VIEWER_REFRESH_RATE));
      }, DURATION_AND_VIEWER_REFRESH_RATE);
    }

    return () => {
      window.clearInterval(intervalId);

      // reset
      durationCounter = 0;
    };
  }, [isActiveSession, timeoutRemainingSec]);

  //
  // handle timeout
  //
  useEffect(() => {
    if (hasTimedOut) {
      setStart(false);
    }
  }, [hasTimedOut]);

  //
  // handle session start callback
  //
  useEffect(() => {
    if (isActiveSession && onSessionStart) {
      onSessionStart();
    }
  }, [isActiveSession, onSessionStart]);

  useEffect(() => {
    const el = document?.getElementsByTagName('html');
    if (el) {
      el[0].setAttribute('style', `font-size: ${theme.typography.htmlFontSize} !important`);
    }
  }, [theme.typography.htmlFontSize]);

  return (
    <Box
      style={!open ? { display: 'none' } : undefined}
      sx={(theme) => ({
        columnGap: 2,
        [theme.breakpoints.up('lg')]: {
          display: 'flex',
        },
      })}
    >
      <Box
        sx={(theme) => ({
          minWidth: theme.breakpoints.values.md / 2,
          flex: `0 0 ${theme.breakpoints.values.md / 2}px`,
        })}
      >
        <Card sx={{ border: 'none' }} variant='outlined'>
          {isActiveSession || connectionInfo || disconnectedInfo ? (
            <CardContent>
              <Text variant='h5' fontWeight={800}>
                Remote-Control session information
              </Text>
              <Box sx={responsiveContentSx}>
                <Box sx={contentSx}>
                  <LegacyDetailsList hideIconsColumn items={disconnectedInfo || connectionInfoItems} />
                </Box>
                <Box sx={contentSx}>
                  <LogFilterForm
                    {...filters}
                    disabled={isActiveSession}
                    bufferList={logBufferList}
                    pidList={pidList}
                    onFilterChange={handleParamsChange}
                  />
                </Box>
              </Box>
              {error && (
                <ControllerAlert severity='error' variant='outlined'>
                  {error}
                </ControllerAlert>
              )}
            </CardContent>
          ) : (
            <CardContent
              sx={{
                '& .MuiCardContent-root': {
                  width: '100%',
                  '&:last-child': {
                    paddingBottom: 1,
                  },
                },
              }}
            >
              <Text variant='h5' fontWeight={800}>
                X-Ray logs
              </Text>
              <Box sx={responsiveContentSx}>
                <Box sx={contentSx}>
                  <Text variant='subtitle1' fontSize='0.875rem'>
                    X-Ray logs allow you to stream logs from live devices in the field. You can filter logs by
                    log level, log buffer, process ID, and tags. Learn more in our{' '}
                    <Link href='https://docs.bymason.com/how-to/xray' target='_blank'>
                      documentation
                    </Link>
                    .
                  </Text>
                </Box>
                <Box sx={contentSx}>
                  <LogFilterForm
                    {...filters}
                    disabled={isActiveSession}
                    bufferList={logBufferList}
                    pidList={pidList}
                    onFilterChange={handleParamsChange}
                  />
                </Box>
              </Box>
              {error && (
                <ControllerAlert severity='error' variant='outlined'>
                  {error}
                </ControllerAlert>
              )}
            </CardContent>
          )}
          <CardActions>
            {isActiveSession ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 2,
                }}
              >
                <Button
                  sx={{ '& .MuiButton-contained': { padding: [0.875, 3, 0.875, 3] } }}
                  classes={{ contained: 'MasonButtonError' }}
                  onClick={handleEndSession}
                  variant='contained'
                >
                  End session
                </Button>
              </Box>
            ) : (
              <Button disabled={isPendingSession} onClick={handleLaunch} variant='contained'>
                Stream Logs
              </Button>
            )}
          </CardActions>
        </Card>
      </Box>
      <Box sx={logSx}>
        {(isPendingSession || isWaitingForStreamCheck) && (
          <LinearProgress
            sx={{
              width: '100%',
              position: 'absolute',
              left: 0,
              top: 0,
            }}
          />
        )}
        {isActiveSession || logLength ? (
          <LogViewer
            hasLineNumbers={!!logLength}
            height='100%'
            data={logLength ? logCollectionRef.current : undefined}
            isTextWrapped={wrapText}
            theme='dark'
            scrollToRow={isFollowing.current ? logLength - 1 : undefined}
            onScroll={handleScroll}
            overScanCount={10}
            toolbar={
              <Toolbar>
                <ToolbarContent>
                  <ToolbarItem>
                    <LogViewerSearch
                      placeholder='Search'
                      minSearchChars={MIN_SEARCH_CHARS}
                      onChange={handleSearch}
                      onClear={handleClearSearch}
                      isDisabled={!logLength}
                    />
                    <Checkbox
                      label='Wrap lines'
                      isChecked={wrapText}
                      id='wrap-text-checkbox'
                      onChange={handleWrapChange}
                    />
                  </ToolbarItem>
                </ToolbarContent>
              </Toolbar>
            }
            footer={
              start &&
              !isFollowing.current && (
                <Button
                  sx={{ '& .MuiButton-contained': { padding: [0.875, 3, 0.875, 3] } }}
                  classes={{ contained: 'MasonButtonError' }}
                  onClick={handleStartFollowing}
                  variant='contained'
                  disabled={isSearching.current}
                >
                  {isSearching.current ? 'Clear Search to follow' : 'Follow'}
                </Button>
              )
            }
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '& > img': {
                width: 260,
              },
            }}
          >
            <img src='https://mcdn.bymason.com/runway/controller/xray-placeholder.png' />
          </Box>
        )}
      </Box>
      <DataUsageWarningModal
        feature='log-stream'
        deviceName={deviceName || deviceId}
        onClose={handleDataUsageWarningClose}
        open={showDataUsageModal}
      />
      <TimeoutWarningModal
        onClose={handleTimeoutWarningClose}
        open={timeoutRemainingSec > 0}
        timeoutRemainingSec={timeoutRemainingSec}
      />
      <TimeoutModal onClose={handleTimeoutClose} open={hasTimedOut} />
    </Box>
  );
}

interface XRayLogStreamProps {
  deviceFamily: string;
  deviceId: string;
  deviceName: string;
  open?: boolean;
  onSessionEnd?: () => void;
  onSessionStart?: () => void;
}

interface OnScrollParams {
  scrollDirection: 'forward' | 'backward';
  scrollOffset: number;
  scrollOffsetToBottom: number;
  scrollUpdateWasRequested: boolean;
}

type LogRow = string;
